import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, } from "react";
import styles from "./CygnusGrid.module.css";
import { ThreeCircles } from "react-loader-spinner";
import { themeQuartz, 
  ModuleRegistry, 
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  GridApi, 
} from "ag-grid-community";
import { CygnusGridProps, CygnusGridRef } from "./CygnusGrid.utils";
import { ServerSideRowModelModule } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";


export const cygnusTheme = themeQuartz.withParams({
  // Typography (affects the whole grid)
  fontFamily: ['"Poppins"', "system-ui"],
  fontSize: "var(--default)", // can be a CSS var or number (px)
  // Headers
  headerFontFamily: ['"Poppins"', "system-ui"],
  headerFontWeight: 400,
  // Colors (UI chrome, headers, tool panels inherit these)
  textColor: "rgba(0,0,0,0.54)",
  headerTextColor: "rgba(0,0,0,0.54)",
});

// Register Server-Side Row Model (required in v33+)
ModuleRegistry.registerModules([ServerSideRowModelModule]);

export const CustomNoRowsOverlay: React.FC = () => {
  return (
    <div className="ag-custom-no-rows">
      🚫 No records found. Please try again.
    </div>
  );
};

export const CustomLoadingOverlay: React.FC = () => {
  return (
    <div className={`ag-custom-loading ${styles.customGridLoader}`}>
      <div>
        <ThreeCircles
          height="50"
          width="50"
          color="#4fa94d"
          wrapperStyle={{}}
          wrapperClass=""
          visible={true}
          ariaLabel="three-circles-rotating"
          outerCircleColor="var(--theme1_tertiary)"
          innerCircleColor="var(--theme1_secondary)"
          middleCircleColor="var(--theme1_primary)"
        />
      </div>
      <div className={styles.customGridLoaderText}>Loading your records</div>
    </div>
  );
};

// Pull a usable value from an AG filter model entry
const getFilterValue = (def: any) => {
  if (!def) return undefined;
  switch (def.filterType) {
    case "set":
      return Array.isArray(def.values) ? def.values : [];
    case "text":
      return def.filter ?? "";
    case "number":
    case "date":
      return def.type === "equals" ? def.filter : undefined;
    default:
      return undefined;
  }
};

// Build your legacy { Group, Status, ... } filter using filterKeyMap and AG's filterModel
const buildLegacyFilterFromModel = (
  filterModel: any,
  filterKeyMap?: Record<string, string>
) => {
  if (!filterModel || !filterKeyMap) return {};
  const out: Record<string, any> = {};
  for (const [colKey, def] of Object.entries<any>(filterModel)) {
    const backendKey = filterKeyMap[colKey]; // colKey should be colId (or field) used in your colDefs
    if (!backendKey) continue;

    const v = getFilterValue(def);
    // Your backend expects a single value today; pick first if 'set' returns many.
    const one = Array.isArray(v) ? v[0] : v;
    if (one !== undefined) out[backendKey] = one;
  }
  return out;
};

const stripEmptySetFilters = (model: Record<string, any> | undefined) => {
  const out: Record<string, any> = {};
  if (!model) return out;
  for (const [k, v] of Object.entries(model)) {
    if (v && v.filterType === "set") {
      if (Array.isArray(v.values) && v.values.length > 0) out[k] = v;
    } else if (v) {
      out[k] = v; // keep non-set filters untouched
    }
  }
  return out;
};

const isEmptyObject = (o: Record<string, any>) => Object.keys(o).length === 0;

const CygnusGrid = forwardRef<CygnusGridRef, CygnusGridProps>(
  (
  {
    columnDefs,
    fetchData,
    rowSelection = "multiple",
    searchText = "",
    sourceCode = "",
    filterKeyMap = {},
    dataIdentifier = "id",
    preSelectedRows =[],
    pageSize = 30,
    // maxBlocksInCache is not used with SSRM; kept in props for compatibility but not passed to grid
    defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 60, // baseline so icons + text fit
      // wrapHeaderText: true, // allow wrapping for long titles
      // autoHeaderHeight: true, // grow header row instead of overlapping
    },
    cachedIsLoading,
    onLoadingChange,
    onSelectionChange,
    cachedData,
    setCachedData,
    onCellValueChanged,
    singleClickEdit = false,
    suppressRowHoverHighlight = false,
    getRowId,
    onRowClicked,
    gridOptions,    
    rowHeight,
    gRef,
    rowModelType = "serverSide",
  },
  ref
) => {

  const gridRef = useRef<AgGridReact>(null);
  const initialCacheRef = useRef<{
    records: any[];
    totalCount: number;
  } | null>(null);

  const hasLoadedOnce = useRef(false);
  const [internalLoading, setInternalLoading] = useState(false);
  const setGridLoading = (loading: boolean) => {
      const api = gridRef.current?.api as any;
      setInternalLoading(loading);
      if (!api) return;
      // v32+: this is the supported way
      api.setGridOption?.("loading", loading);
    };

  const getDataSource = useCallback((): IServerSideDatasource => {
      return {
        getRows: async (params: IServerSideGetRowsParams) => {
          const { startRow, endRow, sortModel, filterModel } =
            (params as any).request || {};
          try {
            if (!hasLoadedOnce.current && startRow === 0) {
              setGridLoading(true);
            }
            onLoadingChange?.(true);

            // If all set-filters are empty → treat as NO FILTER
            const normalizedFilterModel = stripEmptySetFilters(filterModel);
            const isNoFilter = isEmptyObject(normalizedFilterModel);

            // If NO FILTER + we have an initial cache for the first block, serve from cache
            const requestingFirstBlock =
              startRow === 0 && endRow - startRow > 0;
            if (
              isNoFilter &&
              initialCacheRef.current &&
              requestingFirstBlock &&
              (searchText ?? "") === "" && // only short-circuit when not searching
              (!sortModel || sortModel.length === 0) // and not sorting
            ) {
              const { records, totalCount } = initialCacheRef.current;
              setGridLoading(false);
              gridRef.current?.api.hideOverlay(); // make sure "no rows" overlay is hidden
              params.success({ rowData: records, rowCount: totalCount });
              return; // no API call
            }

            // Build backend filter from the *normalized* model
            const legacyFilter = buildLegacyFilterFromModel(
              normalizedFilterModel,
              filterKeyMap
            );

            // Your normal fetch
            const response = await fetchData({
              offset: startRow,
              limit: endRow - startRow,
              sortModel,
              filterModel: normalizedFilterModel,
              searchText,
              sourceCode,
              filter: legacyFilter,
            });

            const { records, totalCount } = response;

            // Keep last block (existing behavior)
            setCachedData?.(records);

            // Capture the initial unfiltered first page for future short-circuits
            if (!hasLoadedOnce.current && isNoFilter && startRow === 0) {
              initialCacheRef.current = { records, totalCount };
            }

            setGridLoading(false);

            if (records.length === 0 && startRow === 0) {
              gridRef.current?.api.showNoRowsOverlay();
            } else {
              gridRef.current?.api.hideOverlay();
              if (!hasLoadedOnce.current) hasLoadedOnce.current = true;
            }

            params.success({ rowData: records, rowCount: totalCount });
          } catch (e) {
            console.error("AG Grid SSRM load error:", e);
            setGridLoading(false);
            gridRef.current?.api.showNoRowsOverlay();
            params.fail();
          } finally {
            onLoadingChange?.(false);
          }
        },
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      fetchData,
      onLoadingChange,
      searchText,
      sourceCode,
      setCachedData,
      filterKeyMap,
    ]);

    // helper
    const autoSizeAll = (api: GridApi) => {
      // defer a tick so headers are fully laid out
      setTimeout(() => {
        // includeHeader: true ensures menu/sort/filter space is accounted for
        // Works in v34 (merged API)
        (api as any).autoSizeAllColumns?.(undefined, /* skipHeader */ false) ?? api.autoSizeAllColumns(); // fallback for typings variance
      }, 0);
    };

  /** Grid Ready */
  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      // Version-proof: set via grid option (works when SSRM is registered)
      // Version-proof: set via grid option (works when SSRM is registered)
      if (rowModelType === "serverSide") {
        params.api.setGridOption("serverSideDatasource", getDataSource());
      }
      params.api.closeToolPanel(); // ensures it starts closed
      autoSizeAll(params.api);
      if (cachedIsLoading) setGridLoading(true); // seed loader
      // 🔁 attach API manually to forwarded ref
      if (gRef && typeof gRef === "object") {
        gRef.current = {
          ...gRef.current,
          api: params.api,
        };
      }
      
    },

    [getDataSource, gRef, cachedIsLoading, rowModelType]
  );

  useEffect(() => {
      if (cachedIsLoading === undefined) return;
      setGridLoading(!!cachedIsLoading);
    }, [cachedIsLoading]);

  /** Pre-select rows after data is available */
  useEffect(() => {
    if (!gridRef.current?.api || !preSelectedRows?.length) return;

    const idSet = new Set(preSelectedRows.map((row) => row[dataIdentifier]));
    setTimeout(() => {
      gridRef.current?.api?.forEachNode((node) => {
        if (idSet.has(node.data?.[dataIdentifier])) {
          node.setSelected(true);
        }
      });
    }, 0);
  }, [preSelectedRows, dataIdentifier]);

  /** Re-fetch on search text change */
  const previousSearchTextRef = useRef<string | null>(null);
  useEffect(() => {
    if (!gridRef.current?.api) return;

    if (previousSearchTextRef.current === null && searchText === "") {
      previousSearchTextRef.current = searchText;
      return;
    }

    if (previousSearchTextRef.current !== searchText) {
      previousSearchTextRef.current = searchText;
      hasLoadedOnce.current = false;
      gridRef.current.api.setGridOption(
        "serverSideDatasource",
        getDataSource()
      );
    }
  }, [getDataSource, searchText]);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    refreshGrid: () => {
      const api = gridRef.current?.api;
      if (!api) return;
      api.deselectAll();
      hasLoadedOnce.current = false;
      api.setGridOption("serverSideDatasource", getDataSource());
    },
    getSelectedRows: () => gridRef.current?.api.getSelectedRows() || [],
    deselectAllRows: () => gridRef.current?.api.deselectAll(),
    applyTransaction: (tx: any) => gridRef.current?.api.applyTransaction(tx),
    api: gridRef.current?.api,
  }));

  // put this once in your app bootstrap
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () =>
      document.body.setAttribute(
        "data-ag-theme-mode",
        media.matches ? "light" : "light"
      );
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, []);

  return (
    <div className={`cygnusGridHost ${styles.cygnusGridContainer}`}>
      <AgGridReact
        theme={cygnusTheme}
        ref={gridRef}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        // 🔁 Enterprise Server-Side Row Model
        rowModelType={rowModelType}
        // 🔢 Pagination (server-side)
        pagination={rowModelType === "serverSide"}
        paginationPageSize={pageSize}
        // Request block size for SSRM
        cacheBlockSize={pageSize}
        // Client-side mode row data
        rowData={rowModelType === "clientSide" ? cachedData : undefined}
        onGridReady={onGridReady}
        onFirstDataRendered={({ api }) => autoSizeAll(api)}        
        autoSizeStrategy={{ type: "fitCellContents" }}
        onSelectionChanged={() => {
          setTimeout(() => {
            const selectedRows = gridRef.current?.api.getSelectedRows() || [];
            onSelectionChange?.(selectedRows);
          }, 0);
        }}
        loadingOverlayComponent={CustomLoadingOverlay}
        noRowsOverlayComponent={CustomNoRowsOverlay}
        rowSelection={rowSelection}
        domLayout="normal"
        rowHeight={rowHeight ?? 25}
        onCellValueChanged={onCellValueChanged}
        singleClickEdit={singleClickEdit}
        suppressRowHoverHighlight={suppressRowHoverHighlight}
        sideBar={{
          toolPanels: ["columns", "filters"],
          // defaultToolPanel: "filters",
        }}
        getRowId={getRowId}
        onRowClicked={onRowClicked}
        {...gridOptions}
      />
    </div>
  );
});

export default CygnusGrid;
